import React, { useEffect, useState } from "react";
import { api, formatValue } from "@/lib/api";
import { Loader2, Flame, ArrowRightLeft, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import PlayerTag from "@/components/PlayerTag";

function heatColor(v) {
  if (v <= 0) return "#F3F4F6";
  const a = Math.min(1, v / 100);
  return `rgba(22,163,74,${0.1 + a * 0.8})`;
}

export default function HeatMapPanel({ id }) {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [pitchTab, setPitchTab] = useState("neutral");
  const [copied, setCopied] = useState(-1);

  useEffect(() => { api.get(`/leagues/${id}/trade-heatmap`).then(r => setD(r.data)).catch(() => setD({ teams: [] })); }, [id]);

  if (d === null) return <div className="card-bordered p-12 flex justify-center" data-testid="heatmap-loading"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>;
  if (!d.teams?.length) return <div className="card-bordered p-8 text-center text-sm text-[var(--text-3)]">Not enough rosters to build a trade heat map.</div>;

  const copy = (text, i) => { navigator.clipboard?.writeText(text); setCopied(i); toast.success("Pitch copied"); setTimeout(() => setCopied(-1), 1500); };

  return (
    <div data-testid="ctx-heatmap">
      <div className="text-sm text-[var(--text-2)] mb-3">
        Complementarity grid — bright cells = your surplus fills their deficit (and vice-versa). Brighter = better trade fit. Your row/column is outlined.
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-bordered p-4 overflow-x-auto">
          <div className="inline-block">
            <div className="flex">
              <div className="w-28 shrink-0" />
              {d.teams.map((t, j) => (
                <div key={j} className="w-9 text-center text-[9px] font-mono text-[var(--text-3)]" title={t.manager}>{j + 1}</div>
              ))}
            </div>
            {d.matrix.map((row, i) => (
              <div key={i} className="flex items-center">
                <div className={`w-28 shrink-0 truncate text-[11px] pr-2 ${d.teams[i].is_user ? "text-[var(--accent)] font-semibold" : "text-[var(--text-2)]"}`} title={d.teams[i].manager}>
                  {i + 1}. {d.teams[i].manager}
                </div>
                {row.map((v, j) => (
                  <button key={j}
                    onClick={() => i !== j && setSel({ i, j, v })}
                    className="w-9 h-9 border border-[var(--border)] flex items-center justify-center text-[9px] font-mono transition-transform hover:scale-110"
                    style={{
                      background: i === j ? "#E5E7EB" : heatColor(v),
                      color: v > 55 ? "#fff" : "var(--text-3)",
                      outline: (d.teams[i].is_user || d.teams[j].is_user) && i !== j ? "1px solid rgba(22,163,74,0.5)" : "none",
                    }}
                    title={i === j ? "" : `${d.teams[i].manager} ↔ ${d.teams[j].manager}: ${v}`}
                    data-testid={`heat-${i}-${j}`}>
                    {i === j ? "" : v || ""}
                  </button>
                ))}
              </div>
            ))}
          </div>
          {sel && (
            <div className="mt-4 text-sm" data-testid="heat-detail">
              <span className="text-[var(--accent)] font-semibold">{d.teams[sel.i].manager}</span>
              <ArrowRightLeft className="w-4 h-4 inline mx-2 text-[var(--text-3)]" />
              <span className="text-[var(--accent)] font-semibold">{d.teams[sel.j].manager}</span>
              <span className="ml-2 font-mono text-[var(--text-3)]">fit {sel.v}/100</span>
              <span className="ml-2 text-xs text-[var(--text-3)]">— {d.teams[sel.i].surplus} surplus meets {d.teams[sel.j].need} need</span>
            </div>
          )}
        </div>

        {/* Counter-offer generator */}
        <div className="card-bordered p-4" data-testid="heat-suggestions">
          <div className="flex items-center gap-2 text-[10px] tracking-widest font-mono text-[var(--accent)] mb-3">
            <Flame className="w-3.5 h-3.5" /> YOUR BEST TRADE FITS
          </div>
          {(!d.suggestions || d.suggestions.length === 0) ? (
            <div className="text-xs text-[var(--text-3)]">Connect the Sleeper account that owns a team in this league to get tailored counter-offers.</div>
          ) : (
            <div className="flex gap-1 mb-3">
              {["neutral", "contender", "rebuilder"].map(p => (
                <button key={p} onClick={() => setPitchTab(p)}
                  className={`text-[10px] tracking-widest font-mono px-2 py-1 rounded-sm border ${pitchTab === p ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-[var(--text-3)]"}`}
                  data-testid={`pitch-tab-${p}`}>{p.toUpperCase()}</button>
              ))}
            </div>
          )}
          {(d.suggestions || []).map((s, i) => (
            <div key={i} className="border-b border-[var(--border)] last:border-0 pb-3 mb-3 last:pb-0 last:mb-0" data-testid={`heat-sugg-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold truncate">{s.partner}</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-[var(--border)] text-[var(--text-3)]">{s.partner_type} · fit {s.fit_score}</span>
              </div>
              <div className="flex flex-col gap-1.5 mb-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-[var(--negative)] font-mono shrink-0">GIVE</span>
                  <PlayerTag player={s.give} size={22} showPos />
                  <span className="text-[var(--text-3)] ml-auto shrink-0">{formatValue(s.give.value)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[var(--accent)] font-mono shrink-0">GET&nbsp;</span>
                  <PlayerTag player={s.get} size={22} showPos />
                  <span className="text-[var(--text-3)] ml-auto shrink-0">{formatValue(s.get.value)}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <p className="text-[11px] text-[var(--text-2)] italic flex-1">{s.pitches[pitchTab]}</p>
                <button onClick={() => copy(s.pitches[pitchTab], i)} className="text-[var(--text-3)] hover:text-[var(--accent)] shrink-0" data-testid={`copy-pitch-${i}`}>
                  {copied === i ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              {s.ai_pitch && (
                <div className="mt-2 rounded-sm border border-[var(--accent)]/25 bg-[var(--accent)]/[0.04] px-2.5 py-2" data-testid={`heat-ai-${i}`}>
                  <div className="flex items-center gap-1 text-[9px] tracking-widest font-mono text-[var(--accent)] mb-1">
                    <Flame className="w-3 h-3" /> AI WHY-NOW
                  </div>
                  <p className="text-[11px] text-[var(--text-2)] leading-relaxed">{s.ai_pitch}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
